import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { flows } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Button, Text, Title, ActionIcon, Badge } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal, openModal, closeModal } from '@repletoryreach/sdk-ui/modals';
import { Switch, Textarea, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../i18n/I18nProvider';
import { formatCount, formatDateTime } from '../../lib/format';
import { formatCredits } from '../../lib/credits';
import { openKbDocument } from './open-document';

const MUTED = 'var(--rr-color-text-muted)';
const errorText = (e: unknown, fallback: string) => {
  const body = (e as { body?: { error?: { message?: string } } } | null)?.body?.error?.message;
  return body ?? (e instanceof Error ? e.message : fallback);
};
const running = (c?: flows.KbConflictCheck) => c?.status === 'queued' || c?.status === 'running';

/** What the correction drawer is editing: a new one, one for a conflict, or an existing one. */
export type CorrectionDraft = { id?: string; conflictId?: string; title: string; answer: string };
type Draft = CorrectionDraft;

/*
 * Accuracy of the knowledge base (owner's decisions of 3 October 2026). Two cards:
 * conflicting information (documents that say different things, found on their own
 * when a document is added or changes, and on request, charged as AI use), and
 * corrections (the business's own word, which the bot puts ahead of every document).
 * A conflict is settled by writing the correct answer, which becomes a correction.
 * The deep check (29 October 2026) compares every fact, and may find a document that
 * disagrees with a correction: that is settled by fixing or removing the document.
 */
export function AccuracyCards({ show }: { show?: 'conflicts' | 'corrections' } = {}) {
  const t = useT();
  const [draft, setDraft] = useState<Draft | null>(null);
  // Each card has its own tab on the Knowledge base page (8 October 2026); both still share the correction drawer.
  return (
    <>
      {show !== 'corrections' && <ConflictsCard onCorrect={(c) => setDraft({ conflictId: c.id, title: c.topic, answer: '' })} />}
      {show !== 'conflicts' && <CorrectionsCard onEdit={setDraft} />}
      <CorrectionDrawer draft={draft} onClose={() => setDraft(null)} t={t} />
    </>
  );
}

function ConflictsCard({ onCorrect }: { onCorrect: (c: flows.KbConflict) => void }) {
  const t = useT();
  const qc = useQueryClient();
  const checks = useQuery({
    queryKey: ['kb-conflict-checks'],
    queryFn: () => flows.listKbConflictChecks(),
    refetchInterval: (q) => ((q.state.data?.data ?? []).some(running) ? 3000 : 30000)
  });
  const latest = checks.data?.data?.[0];
  const conflicts = useQuery({
    queryKey: ['kb-conflicts', latest?.id, latest?.status],
    queryFn: () => flows.listKbConflicts('open')
  });
  const open = conflicts.data?.data ?? [];
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['kb-conflicts'] });
    void qc.invalidateQueries({ queryKey: ['kb-conflict-checks'] });
  };
  const dismiss = useMutation({
    mutationFn: (c: flows.KbConflict) => flows.dismissKbConflict(c.id),
    onSuccess: () => { refresh(); notifySuccess(t('knowledge.conflicts.dismissed'), t('knowledge.conflicts.title')); },
    onError: (e) => notifyError(errorText(e, t('common.unknownError')), t('knowledge.conflicts.title'))
  });
  const start = useMutation({
    mutationFn: (mode: flows.KbConflictMode) => flows.startKbConflictCheck(mode),
    onSuccess: (_r, mode) => {
      refresh();
      notifySuccess(t(mode === 'deep' ? 'knowledge.conflicts.startedDeep' : 'knowledge.conflicts.started'), t('knowledge.conflicts.title'));
    },
    onError: (e) => notifyError(errorText(e, t('common.unknownError')), t('knowledge.conflicts.title'))
  });
  const [pricing, setPricing] = useState(false);
  const [opening, setOpening] = useState<string | null>(null);

  /*
   * Check everything offers two checks (29 October 2026), each priced when the choice opens: the quick check (the
   * closest passages of documents that cover the same ground) and the deep check (every fact in every document and
   * correction). A deep estimate that cannot be had leaves the quick check on offer.
   */
  async function checkEverything() {
    setPricing(true);
    try {
      const [quick, deep] = await Promise.all([
        flows.estimateKbConflictCheck('quick').then((r) => r.data),
        flows.estimateKbConflictCheck('deep').then((r) => r.data, () => null)
      ]);
      if (quick.pairs === 0 && (!deep || deep.documents < 2)) { notifySuccess(t('knowledge.conflicts.nothing'), t('knowledge.conflicts.title')); return; }
      let id = '';
      id = openModal({
        title: t('knowledge.conflicts.confirmTitle'),
        children: (
          <CheckChoice quick={quick} deep={deep}
            onStart={(mode) => { closeModal(id); start.mutate(mode); }}
            onCancel={() => closeModal(id)} />
        )
      });
    } catch (err) {
      notifyError(errorText(err, t('common.unknownError')), t('knowledge.conflicts.title'));
    } finally {
      setPricing(false);
    }
  }

  /** A deep finding against a correction is settled in the document: open it as the Documents tab does. */
  async function openDocument(c: flows.KbConflict) {
    setOpening(c.id);
    try {
      await openKbDocument({ id: c.first_document_id, name: c.first_document_name ?? '' });
    } catch (err) {
      notifyError(errorText(err, t('common.unknownError')), t('knowledge.conflicts.title'));
    } finally {
      setOpening(null);
    }
  }

  const deepRun = latest?.mode === 'deep';
  return (
    <Card withBorder data-testid="kb-conflicts">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
          <Stack gap={2} style={{ flex: 1, minWidth: 240 }}>
            <Title order={4}>{t('knowledge.conflicts.title')}</Title>
            <Text role="bodySmall" c={MUTED}>{t('knowledge.conflicts.intro')}</Text>
          </Stack>
          <Button size="sm" variant="default" loading={pricing || start.isPending} disabled={running(latest)} onClick={() => void checkEverything()} data-testid="kb-conflicts-check">
            {t('knowledge.conflicts.checkAll')}
          </Button>
        </Group>
        {latest && (
          <Text role="caption" c={latest.status === 'skipped' || latest.status === 'failed' ? 'var(--rr-color-warning-text, #b45309)' : MUTED} data-testid="kb-conflicts-latest">
            {running(latest)
              ? deepRun ? deepProgress(latest, t) : t('knowledge.conflicts.checking')
              : latest.status === 'skipped'
                ? t('knowledge.conflicts.skipped', { when: formatDateTime(latest.created_at) })
                : latest.status === 'failed'
                  ? t(deepRun ? 'knowledge.conflicts.failedDeep' : 'knowledge.conflicts.failed', { when: formatDateTime(latest.created_at) })
                  : t(deepRun ? 'knowledge.conflicts.lastCheckedDeep' : 'knowledge.conflicts.lastCheckedQuick', { when: formatDateTime(latest.finished_at ?? latest.created_at) })}
          </Text>
        )}
        {conflicts.error && <Alert color="error" role="alert">{(conflicts.error as Error).message}</Alert>}
        {!conflicts.isPending && open.length === 0 && <EmptyState title={t('knowledge.conflicts.empty')} description={t('knowledge.conflicts.emptyHint')} />}
        {open.map((c) => {
          // A deep finding may set a document against the business's own correction rather than another document.
          const againstCorrection = !c.second_document_id && (!!c.second_correction_id || !!c.second_correction_title);
          const secondName = againstCorrection
            ? t('knowledge.conflicts.yourCorrection', { title: c.second_correction_title ?? c.topic })
            : c.second_document_name ?? t('knowledge.conflicts.removedDocument');
          return (
            <div key={c.id} className="rr-knowledge__source" data-testid="kb-conflict">
              <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
                <Group gap="xs" wrap="wrap">
                  <Text role="bodySmall" fw={650}>{c.topic}</Text>
                  {c.found_by === 'deep' && <Badge size="xs" variant="light" color="violet" data-testid="kb-conflict-deep">{t('knowledge.conflicts.foundByDeep')}</Badge>}
                </Group>
                <ConflictSide name={c.first_document_name ?? t('knowledge.conflicts.removedDocument')} says={c.first_says} quote={c.first_quote} />
                <ConflictSide name={secondName} says={c.second_says} quote={c.second_quote} testId={againstCorrection ? 'kb-conflict-correction' : undefined} />
                {c.reason && <Text role="caption" c={MUTED} data-testid="kb-conflict-reason">{t('knowledge.conflicts.reason', { reason: c.reason })}</Text>}
                {againstCorrection && <Text role="caption" data-testid="kb-conflict-correction-hint">{t('knowledge.conflicts.correctionHint')}</Text>}
              </Stack>
              <Group gap={4} wrap="wrap" justify="flex-end">
                {againstCorrection
                  ? c.first_document_name && (
                    <Button size="xs" loading={opening === c.id} onClick={() => void openDocument(c)} data-testid="kb-conflict-open">{t('knowledge.conflicts.openDocument')}</Button>
                  )
                  : <Button size="xs" onClick={() => onCorrect(c)} data-testid="kb-conflict-correct">{t('knowledge.conflicts.correct')}</Button>}
                <Button size="xs" variant="default" loading={dismiss.isPending && dismiss.variables?.id === c.id} onClick={() => dismiss.mutate(c)}>
                  {t('knowledge.conflicts.dismiss')}
                </Button>
              </Group>
            </div>
          );
        })}
      </Stack>
    </Card>
  );
}

/** One side of a conflict: whose it is and what it says, then (a deep finding) its exact words, muted. */
function ConflictSide({ name, says, quote, testId }: { name: string; says: string; quote?: string | null; testId?: string }) {
  const t = useT();
  return (
    <Stack gap={0} data-testid={testId}>
      <Text role="caption"><b>{name}:</b> {says}</Text>
      {quote && <Text role="caption" c={MUTED} data-testid="kb-conflict-quote">{t('knowledge.conflicts.quote', { quote })}</Text>}
    </Stack>
  );
}

/** A running deep check's progress: reading the documents and corrections, then comparing the groups of facts. */
function deepProgress(c: flows.KbConflictCheck, t: ReturnType<typeof useT>): string {
  const total = c.documents_total ?? 0;
  const groups = c.groups_total ?? 0;
  if (c.status !== 'running' || (total === 0 && groups === 0)) return t('knowledge.conflicts.checkingDeep');
  const vars = {
    read: formatCount(c.documents_read ?? 0), total: formatCount(total), facts: formatCount(c.facts_found ?? 0),
    judged: formatCount(c.groups_judged ?? 0), groups: formatCount(groups)
  };
  if (groups === 0) return t('knowledge.conflicts.progressReading', vars);
  return t(total === 0 ? 'knowledge.conflicts.progressComparing' : 'knowledge.conflicts.progressJudging', vars);
}

/**
 * The choice Check everything opens: the quick check and the deep check side by side (one above the other on a
 * phone), each with what it reads, what it costs and its own Start button, which is off when the credit cannot pay.
 */
function CheckChoice({ quick, deep, onStart, onCancel }: {
  quick: flows.KbConflictEstimate; deep: flows.KbConflictEstimate | null;
  onStart: (mode: flows.KbConflictMode) => void; onCancel: () => void;
}) {
  const t = useT();
  const cost = (e: flows.KbConflictEstimate) => e.priced
    ? t('knowledge.conflicts.cost', { amount: formatCredits(e.estimated_minor, t, e.currency) })
    : t('knowledge.conflicts.costUnknown');
  const short = (e: flows.KbConflictEstimate) => e.priced && !e.affordable;
  const quickOk = quick.pairs > 0;
  const deepOk = !!deep && deep.documents >= 2;
  const toRead = deep?.to_read ?? 0;
  const facts = deep?.facts ?? 0;
  const groups = deep?.groups ?? 0;
  return (
    <Stack gap="sm" data-testid="kb-conflicts-choice">
      <Text role="bodySmall" c={MUTED}>{t('knowledge.conflicts.chooseIntro')}</Text>

      <div className="rr-knowledge__snippet" data-testid="kb-check-quick">
        <Text role="bodySmall" fw={700}>{t('knowledge.conflicts.quick.title')}</Text>
        <Text role="caption" c={MUTED}>{t('knowledge.conflicts.quick.about')}</Text>
        {quickOk ? (
          <>
            <Text role="bodySmall">{quick.pairs === 1 ? t('knowledge.conflicts.confirmBodyOne') : t('knowledge.conflicts.confirmBody', { pairs: formatCount(quick.pairs), documents: formatCount(quick.documents) })}</Text>
            <Text role="bodySmall" fw={650} data-testid="kb-conflicts-estimate">{cost(quick)}</Text>
            {short(quick) && <Alert color="warning">{t('knowledge.conflicts.notEnough')}</Alert>}
          </>
        ) : (
          <Text role="bodySmall">{t('knowledge.conflicts.nothing')}</Text>
        )}
        <Group justify="flex-end">
          <Button size="sm" variant="default" disabled={!quickOk || short(quick)} onClick={() => onStart('quick')} data-testid="kb-conflicts-run-quick">
            {t('knowledge.conflicts.quick.run')}
          </Button>
        </Group>
      </div>

      <div className="rr-knowledge__snippet" data-testid="kb-check-deep">
        <Text role="bodySmall" fw={700}>{t('knowledge.conflicts.deep.title')}</Text>
        <Text role="caption" c={MUTED}>{t('knowledge.conflicts.deep.about')}</Text>
        {!deep && <Text role="bodySmall">{t('knowledge.conflicts.deep.unavailable')}</Text>}
        {deep && !deepOk && <Text role="bodySmall">{t('knowledge.conflicts.deep.nothing')}</Text>}
        {deep && deepOk && (
          <>
            <Text role="bodySmall">
              {toRead === 0 ? t('knowledge.conflicts.deep.toReadNone')
                : toRead === 1 ? t('knowledge.conflicts.deep.toReadOne')
                : t('knowledge.conflicts.deep.toRead', { count: formatCount(toRead) })}
            </Text>
            {facts > 0 && <Text role="bodySmall">{t('knowledge.conflicts.deep.facts', { count: formatCount(facts) })}</Text>}
            {(groups > 0 || deep.groups_known) && (
              <Text role="bodySmall">{t(deep.groups_known ? 'knowledge.conflicts.deep.groups' : 'knowledge.conflicts.deep.groupsAbout', { count: formatCount(groups) })}</Text>
            )}
            <Text role="caption" c={MUTED} data-testid="kb-check-deep-models">
              {t(deep.judge_fallback ? 'knowledge.conflicts.deep.modelsFallback' : 'knowledge.conflicts.deep.models')}
            </Text>
            <Text role="caption" c={MUTED}>{t('knowledge.conflicts.deep.reuse')}</Text>
            {deep.capped && <Alert color="info">{t('knowledge.conflicts.deep.capped')}</Alert>}
            <Text role="bodySmall" fw={650} data-testid="kb-conflicts-estimate-deep">{cost(deep)}</Text>
            {short(deep) && <Alert color="warning">{t('knowledge.conflicts.notEnough')}</Alert>}
          </>
        )}
        <Group justify="flex-end">
          <Button size="sm" disabled={!deepOk || (!!deep && short(deep))} onClick={() => onStart('deep')} data-testid="kb-conflicts-run-deep">
            {t('knowledge.conflicts.deep.run')}
          </Button>
        </Group>
      </div>

      <Group justify="flex-end">
        <Button variant="subtle" onClick={onCancel}>{t('action.cancel')}</Button>
      </Group>
    </Stack>
  );
}

function CorrectionsCard({ onEdit }: { onEdit: (d: Draft) => void }) {
  const t = useT();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['kb-corrections'], queryFn: () => flows.listKbCorrections() });
  const rows = list.data?.data ?? [];
  const toggle = useMutation({
    mutationFn: (c: flows.KbCorrection) => flows.updateKbCorrection(c.id, { is_active: !c.is_active }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['kb-corrections'] }),
    onError: (e) => notifyError(errorText(e, t('common.unknownError')), t('knowledge.corrections.title'))
  });
  const remove = useMutation({
    mutationFn: (c: flows.KbCorrection) => flows.deleteKbCorrection(c.id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['kb-corrections'] }); notifySuccess(t('knowledge.corrections.removed'), t('knowledge.corrections.title')); },
    onError: (e) => notifyError(errorText(e, t('common.unknownError')), t('knowledge.corrections.title'))
  });
  return (
    <Card withBorder data-testid="kb-corrections">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
          <Stack gap={2} style={{ flex: 1, minWidth: 240 }}>
            <Title order={4}>{t('knowledge.corrections.title')}</Title>
            <Text role="bodySmall" c={MUTED}>{t('knowledge.corrections.intro')}</Text>
          </Stack>
          <Button size="sm" onClick={() => onEdit({ title: '', answer: '' })} data-testid="kb-correction-add">{t('knowledge.corrections.add')}</Button>
        </Group>
        {list.error && <Alert color="error" role="alert">{(list.error as Error).message}</Alert>}
        {!list.isPending && rows.length === 0 && <EmptyState title={t('knowledge.corrections.empty')} description={t('knowledge.corrections.emptyHint')} />}
        {rows.map((c) => (
          <div key={c.id} className="rr-knowledge__source" data-testid="kb-correction">
            <Stack gap={2} style={{ minWidth: 0, flex: 1, opacity: c.is_active ? 1 : 0.6 }}>
              <Text role="bodySmall" fw={650}>{c.title}</Text>
              <Text role="caption" style={{ whiteSpace: 'pre-wrap' }}>{c.answer}</Text>
            </Stack>
            <Group gap={6} wrap="nowrap" align="center">
              <Switch size="sm" checked={c.is_active} onChange={() => toggle.mutate(c)} aria-label={t('knowledge.corrections.inUse', { title: c.title })} />
              <Button size="xs" variant="default" onClick={() => onEdit({ id: c.id, title: c.title, answer: c.answer })}>{t('action.edit')}</Button>
              <ActionIcon
                variant="subtle" size="sm" color="error" aria-label={t('knowledge.corrections.remove', { title: c.title })}
                onClick={() => openConfirmModal({
                  title: t('knowledge.corrections.removeTitle'),
                  children: <Text role="bodySmall">{t('knowledge.corrections.removeBody', { title: c.title })}</Text>,
                  labels: { confirm: t('action.delete'), cancel: t('action.cancel') },
                  confirmProps: { color: 'red' },
                  onConfirm: () => remove.mutate(c)
                })}
              >×</ActionIcon>
            </Group>
          </div>
        ))}
      </Stack>
    </Card>
  );
}

/** Also opened from a chat insights report (29 October 2026): Answer this, with the unanswered question as its title. */
export function CorrectionDrawer({ draft, onClose, t }: { draft: Draft | null; onClose: () => void; t: ReturnType<typeof useT> }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [answer, setAnswer] = useState('');
  const [seeded, setSeeded] = useState<Draft | null>(null);
  if (draft !== seeded) { setSeeded(draft); setTitle(draft?.title ?? ''); setAnswer(draft?.answer ?? ''); }
  const save = useMutation({
    mutationFn: () => draft?.id
      ? flows.updateKbCorrection(draft.id, { title: title.trim(), answer: answer.trim() })
      : flows.createKbCorrection({ title: title.trim(), answer: answer.trim(), conflict_id: draft?.conflictId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kb-corrections'] });
      void qc.invalidateQueries({ queryKey: ['kb-conflicts'] });
      notifySuccess(t(draft?.conflictId ? 'knowledge.corrections.savedConflict' : 'knowledge.corrections.saved'), t('knowledge.corrections.title'));
      onClose();
    },
    onError: (e) => notifyError(errorText(e, t('common.unknownError')), t('knowledge.corrections.title'))
  });
  return (
    <Drawer opened={draft !== null} onClose={onClose} position="right" size="md"
      title={t(draft?.id ? 'knowledge.corrections.editTitle' : draft?.conflictId ? 'knowledge.corrections.conflictTitle' : 'knowledge.corrections.addTitle')}>
      <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <Stack gap="md">
          {draft?.conflictId && <Text role="bodySmall" c={MUTED}>{t('knowledge.corrections.conflictIntro')}</Text>}
          <TextInput label={t('knowledge.corrections.about')} description={t('knowledge.corrections.aboutHint')} value={title}
            maxLength={200} required onChange={(e) => setTitle(e.currentTarget.value)} data-testid="kb-correction-title" />
          <Textarea label={t('knowledge.corrections.answer')} description={t('knowledge.corrections.answerHint')} value={answer}
            minRows={3} maxLength={2000} required onChange={(e) => setAnswer(e.currentTarget.value)} data-testid="kb-correction-answer" />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
            <Button type="submit" loading={save.isPending} disabled={title.trim().length < 2 || answer.trim().length < 2} data-testid="kb-correction-save">
              {t('knowledge.corrections.save')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}
